import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";

type ExitEmailDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string) => Promise<void> | void;
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ExitEmailDialog({ isOpen, onClose, onSubmit }: ExitEmailDialogProps) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    try {
      setSubmitting(true);
      await onSubmit(email);
      onClose();
    } catch (err) {
      // swallow errors but show a message
      setError("Failed to save email");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Before you go</DialogTitle>
          <DialogDescription>
            Leave your email and we'll keep you updated about your quote and
            next steps.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 p-4">
          <label className="block text-sm font-medium text-muted-foreground">Email address</label>
          <input
            className="mt-1 block w-full rounded-md border p-2"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@solarcloud.com.au"
            type="email"
          />
          {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
        </div>

        <DialogFooter>
          <div className="flex w-full justify-end space-x-2">
            <Button variant="ghost" onClick={onClose} disabled={submitting}>
              Continue without saving
            </Button>
            <Button variant="default" onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Saving…" : "Save and continue"}
            </Button>
          </div>
        </DialogFooter>

        <DialogClose />
      </DialogContent>
    </Dialog>
  );
}
